'use client';

import type { SyntheticEvent } from 'react';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import styled, { css } from 'styled-components';
import { useIsLgOrGreater } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { useTranslation } from '@/app/components/translations/Translation';
import TransportJourneySummary, {
  FootprintBadgeContainer,
  FootprintContainer,
} from '@/app/components/hotel-detail/flight-train/TransportJourneySummary';
import { useOffersContext } from '@/app/components/hotel-detail/OffersProvider';
import { DictionaryProvider, FootprintBadge } from '@lmn/lmn-components';
import { URLParameters } from '@/app/common/urlParameters';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { Button, Link } from '@lmn/design-system';
import { Icon } from '@/app/common/icons/Icon';
import { useChangeTransportDetailsOpenModal } from './ChangeTransportDetailsModalProvider';
import { MoreTripThanHotelNightsMessage } from '../flight-train/MoreTripThanHotelNightsMessage';
import { getPriceTypeText } from '../flight-train/TransportCommon';
import { useInitialParams } from '../InitialParamsProvider';
import { generateSearchUrlWithTransport } from '../common/searchUrlGenerator';
import { keyUpHandler } from '../common/common.helper';
import { Price, PriceWithTextContainer, TextPP } from './Price';

const DebugBertolliFormula = dynamic(
  () => import(/* webpackChunkName: "DebugBertolliFormula" */ '@/app/components/hotel-detail/DebugBertolliFormula'),
  {
    ssr: false,
  }
);

// #region styled components
export const TransportCardPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 206px;
  min-width: 206px;
  padding: 16px;
  align-items: flex-end;
  cursor: pointer;
  background-color: ${({ theme }) => theme.commons.colors.gray0};
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
`;

export const TransportCardPriceContainerMobile = styled.div<{ $hideBorder?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  padding: 12px 16px 16px;
  row-gap: 12px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
  ${(p) => css`
    ${!p.$hideBorder && `border-top: 1px solid ${p.theme.commons.colors.gray100};`}
  `}
`;

const PriceContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row-reverse;
`;

const StyledButton = styled(Button)`
  width: 100%;
  min-height: 48px;
`;
// #endregion

export type ChangeTransportCardProps = {
  co2Footprint: CO2Footprint;
  inbound: TransportBoundType;
  isEnabled?: boolean;
  isTrain: boolean;
  onOpenDetailsClick: (event?: SyntheticEvent<Element, Event>) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  onSelectClick?: () => void;
  outbound: TransportBoundType;
  price: string;
  transportId: string;
  realPrice?: number;
  priceFromAPI?: number;
  numberOfBaggagePiecesIncluded?: number;
};

export default function ChangeTransportCard(props: ChangeTransportCardProps) {
  const {
    inbound,
    outbound,
    co2Footprint,
    transportId,
    price,
    isEnabled = true,
    onOpenDetailsClick,
    isTrain,
    realPrice,
    priceFromAPI,
    numberOfBaggagePiecesIncluded,
  } = props;
  const isMobile = !useIsLgOrGreater();
  const { t, tripCardDictionary } = useTranslation();
  const initialParams = useInitialParams();
  const { offersData } = useOffersContext();
  const searchParams = useSearchParams();
  const debug = searchParams.get(URLParameters.DEBUG);
  const priceTypeText = getPriceTypeText(t, offersData?.priceType || initialParams.priceType);
  const { localization } = usePageWrapperData();

  const featureToggles = searchParams.get(URLParameters.FEATURE_TOGGLES)?.split(',') || [];
  const debugSearchLink = featureToggles.includes('debug_telesales_links');
  const showSearchWithFlightLink = localization.isTelesales || debugSearchLink;
  const searchUrl = generateSearchUrlWithTransport(window.location.href, transportId);
  const showCo2 = co2Footprint && co2Footprint.co2kg > 0;
  const { openCo2Modal } = useChangeTransportDetailsOpenModal();

  return (
    <div
      css={css`
        display: flex;
        position: relative;
        border: 1px solid ${({ theme }) => theme.commons.colors.gray100};
        flex-direction: column;
        background-color: ${({ theme }) => theme.commons.colors.gray0};
        border-radius: 0.5rem;
        box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
      `}
    >
      {debug && !isTrain && (
        <DebugBertolliFormula
          flightId={transportId}
          cheapestPrice={offersData?.cheapestMealPlan?.finalPrice}
          price={realPrice}
          priceFromAPI={priceFromAPI}
        />
      )}
      {isMobile ? (
        <>
          <TransportJourneySummary
            outbound={outbound}
            inbound={inbound}
            co2Footprint={co2Footprint}
            transportId={transportId}
            openDetailsClicked={onOpenDetailsClick}
            onCo2InfoClick={showCo2 ? () => openCo2Modal(co2Footprint) : undefined}
            numberOfBaggagePiecesIncluded={numberOfBaggagePiecesIncluded}
            css={`
              flex-grow: 1;
            `}
          />
          <DictionaryProvider labels={tripCardDictionary}>
            <TransportCardPriceContainerMobile onClick={onOpenDetailsClick}>
              <PriceContainer>
                <PriceWithTextContainer>
                  <Price>{price}</Price>
                  <TextPP>{priceTypeText}</TextPP>
                </PriceWithTextContainer>
              </PriceContainer>
              <StyledButton variant="primary" data-testid="select-flight-button">
                {t('vacation.select.details.book')}
              </StyledButton>
            </TransportCardPriceContainerMobile>
          </DictionaryProvider>
          <MoreTripThanHotelNightsMessage inbound={inbound} outbound={outbound} onMessageClicked={onOpenDetailsClick} />
        </>
      ) : (
        <div>
          <div
            css={`
              display: flex;
              flex-direction: row;
            `}
          >
            <TransportJourneySummary
              outbound={outbound}
              inbound={inbound}
              co2Footprint={co2Footprint}
              transportId={transportId}
              openDetailsClicked={onOpenDetailsClick}
              onCo2InfoClick={showCo2 ? () => openCo2Modal(co2Footprint) : undefined}
              numberOfBaggagePiecesIncluded={numberOfBaggagePiecesIncluded}
              css={`
                display: flex;
                flex-direction: column;
                justify-content: center;
                flex-grow: 1;

                [class*='FullTransportSummary__SummaryColumn'] {
                  border-top-right-radius: 0px;
                  border-bottom-right-radius: 0px;
                }
              `}
            />
            <DictionaryProvider labels={tripCardDictionary}>
              <TransportCardPriceContainer onClick={onOpenDetailsClick}>
                {showCo2 ? (
                  <FootprintContainer>
                    <FootprintBadgeContainer
                      $desktop={!isMobile}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        openCo2Modal(co2Footprint);
                      }}
                      onKeyUp={keyUpHandler(() => openCo2Modal(co2Footprint))}
                    >
                      <FootprintBadge footprint={co2Footprint} useShortVariant={true} locale={localization.locale} />
                      <Icon category="Semantics" name="icon-circle-info" size={16} color="iconPrimary" alt="" />
                    </FootprintBadgeContainer>
                  </FootprintContainer>
                ) : null}
                <div
                  css={`
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    width: 100%;
                    margin-top: auto;
                  `}
                >
                  <PriceWithTextContainer>
                    <Price>{price}</Price>
                    <TextPP>{priceTypeText}</TextPP>
                  </PriceWithTextContainer>
                  <StyledButton variant="primary" data-testid="select-flight-button">
                    {t('vacation.select.details.book')}
                  </StyledButton>
                </div>
                {(showSearchWithFlightLink || debugSearchLink) && (
                  <Link
                    variant="secondary"
                    appearance="underlined"
                    href={searchUrl}
                    target="_blank"
                    rel="noreferrer"
                    css="text-align: right;"
                    onClick={(e) => e.stopPropagation()}
                  >
                    new search with this flight
                  </Link>
                )}
              </TransportCardPriceContainer>
            </DictionaryProvider>
          </div>
          <MoreTripThanHotelNightsMessage inbound={inbound} outbound={outbound} onMessageClicked={onOpenDetailsClick} />
        </div>
      )}
      {!isEnabled && (
        <div
          css={`
            position: absolute;
            inset: 0;
            background-color: white;
            opacity: 0.8;
          `}
        />
      )}
    </div>
  );
}
